import { Button } from "design-system/atoms/Button"
import { Input, type InputProps } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { cn } from "design-system/utils"
import EmojiPicker, { type EmojiClickData } from "emoji-picker-react"
import { SmilePlus, X } from "lucide-react"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

interface EmojiInputProps extends Omit<InputProps, "value" | "onChange"> {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmojiSelect?: (emoji: string) => void
}

export function EmojiInput({
  value,
  onChange,
  onEmojiSelect,
  className,
  name,
  ...props
}: EmojiInputProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const emojis = value ? value.split(",").filter(Boolean) : ["❤️"]

  const onSelectEmoji = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji
    const newValue = value ? `${value},${emoji}` : emoji

    if (emojis.includes(emoji)) return

    onEmojiSelect?.(emoji)

    const event = {
      target: { value: newValue, name },
    } as React.ChangeEvent<HTMLInputElement>
    onChange(event)
  }

  const removeEmoji = (event: React.MouseEvent, index: number) => {
    event.stopPropagation()
    event.preventDefault()

    const newEmojis = emojis.filter((_, i) => i !== index)
    const newValue = newEmojis.join(",")

    onChange({
      target: { value: newValue, name },
    } as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <div className="relative flex items-center">
      <div className={cn("relative w-full", className)}>
        <div className="flex gap-1 p-2 min-h-10 border border-input bg-background rounded-md items-start justify-center">
          <div className="flex flex-wrap flex-row items-left flex-1 gap-1">
            {emojis.map((emoji, index) => (
              <Button
                key={index}
                size="icon"
                variant="secondary"
                onClick={(event) => emojis.length > 1 && removeEmoji(event, index)}
                className={cn(
                  "group relative",
                  emojis.length === 1 && "cursor-default select-none",
                )}
              >
                <span className="sr-only">{t("shared.emojis.remove", { emoji })}</span>
                <span className="mt-0.5 text-lg">{emoji}</span>
                {emojis.length !== 1 && (
                  <div className="absolute w-full h-full top-0 left-0 opacity-0 group-hover:opacity-100 group-hover:bg-background/75 flex items-center justify-center transition-opacity">
                    <X className="w-5! h-5! text-foreground" />
                  </div>
                )}
              </Button>
            ))}
          </div>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="default" type="button">
                <span>{t("shared.emojis.add")}</span>
                <SmilePlus className="size-6 hover:text-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" side="bottom" align="start">
              <EmojiPicker
                onEmojiClick={onSelectEmoji}
                autoFocusSearch={false}
                height={350}
                searchDisabled={false}
                skinTonesDisabled={true}
                previewConfig={{ showPreview: false }}
              />
            </PopoverContent>
          </Popover>
        </div>
        {/* Hidden input to maintain form compatibility */}
        <Input
          value={value}
          onChange={onChange}
          name={name}
          className="sr-only"
          tabIndex={-1}
          {...props}
        />
      </div>
    </div>
  )
}
